import type { Message } from "@nexus/types"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { loadChatMessages } from "@/common/services/chat"
import type { ReduxMessage } from "@/common/types/chat"
import { handleError } from "@/common/utils/error-handler"

import type { PlaygroundChatState } from "../playgroundTypes"

interface LoadMoreMessagesParams {
  chatId: string
  chatRoomId: string
}

interface LoadMoreMessagesResult {
  chatId: string
  messages: ReduxMessage[]
  hasMore: boolean
}

/**
 * Load more historical messages for infinite scroll
 * Fetches messages older than the oldest currently loaded message
 */
export const loadMorePlaygroundMessages = createAsyncThunk<
  LoadMoreMessagesResult,
  LoadMoreMessagesParams,
  { state: { playgroundChat: PlaygroundChatState }; rejectValue: { message: string } }
>(
  "playgroundChat/loadMoreMessages",
  async ({ chatId, chatRoomId }, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const chat = state.playgroundChat.chats[chatId]

      if (!chat) {
        throw new Error("Chat not found")
      }

      if (!chat.hasMoreHistory) {
        return { chatId, messages: [], hasMore: false }
      }

      // Get the oldest message ID as cursor
      // Messages are displayed oldest-first, so first message is oldest
      const oldestMessage = chat.messages[0]
      if (!oldestMessage) {
        return { chatId, messages: [], hasMore: false }
      }

      console.log(
        `🔍 [loadMoreMessages] Loading more messages before: ${oldestMessage.id} (${oldestMessage.createdAt})`
      )

      const response = await loadChatMessages({
        chatRoomId,
        limit: 50,
        order: "desc", // Get older messages (before cursor)
        before: oldestMessage.id
      })

      const messages = (response.messages || []) as Message[]
      const hasMore = response.hasMore ?? false

      // Reverse to maintain chronological order (oldest to newest)
      // ReduxMessage = Omit<Message, "user" | "agent"> - messages from API match this
      const reversedMessages = messages.reverse() as ReduxMessage[]

      console.log(
        `✅ [loadMoreMessages] Loaded ${reversedMessages.length} more messages, hasMore: ${hasMore}`
      )

      return {
        chatId,
        messages: reversedMessages,
        hasMore
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load more messages"
      handleError(error, {
        component: "loadMorePlaygroundMessagesThunk",
        operation: "loadMoreMessages",
        silent: true
      })
      return rejectWithValue({
        message: errorMessage
      })
    }
  }
)
